import {StyledListContainer, StyledListItem, StyledTooltipContainer, StyledTooltipTitle} from './Styles'

export const SECTIONS = {
  HEALTH_CONDITIONS: 'Health Conditions',
  COMMUNITY_FACTORS: 'Community Factors',
  USE_CASES: 'Use Cases',
  POINT_OF_INTERESTS: 'Point of Interests',
  PERSONAS: 'Personas',
  INDIVIDUAL_PROFILES: 'Individual Profile',
  INDIVIDUAL_HEALTH_FILTERS: 'Individual Health',
  HOME_AND_HOUSEHOLD_FILTERS: 'Home and Household',
  FINANCIAL_WELLBEING: 'Financial Well-Being',
  RESOURCE_ACCESS_BARRIERS: 'Resource Access Barriers and Support',
  COMMUNITY_SUPPORT: 'Community Support and Location',
  ENVIRONMENTAL_EXPOSURE_FILTERS: 'Environmental Exposure'
}

// Helper function to generate section keys mapping from filter config
// Maps section title to section id (e.g., "Individual Profile" -> "1")
const getSectionKeys = (filterConfig) => {
  if (!filterConfig?.data) return {}
  return filterConfig.data.reduce((acc, section) => {
    if (section.title && section.id) {
      acc[section.title] = section.id
    }
    return acc
  }, {})
}

export const PERMISSIONS = {
  FILTERS: 5,
  USE_CASES: 13,
  POINT_OF_INTERESTS: 12,
  PERSONAS: 14
}

export const getId = (type, name) => {
  return `${type}_${name}`
}

export const getValidData = (data) => {
  if (!data || !Array.isArray(data)) return []
  return data.filter((obj) => obj.id && Number.parseInt(obj.id) > 0)
}

export const formatHealthConditions = (config) => {
  if (config?.riskCentiles) {
    const children = Object.keys(config.riskCentiles).map((key) => ({
      id: key,
      name: key,
      children: []
    }))
    return [
      {
        isTitle: true,
        name: config.title,
        children
      }
    ]
  }
}

export const formatSectionFilters = (config, sectionTitle) => {
  if (config?.data) {
    const section = config.data.find((item) => item.title === sectionTitle)
    if (section && Array.isArray(section.filters)) {
      const children = section.filters.map((filter) => ({
        id: filter.key,
        name: filter.name,
        children: []
      }))
      return [
        {
          isTitle: true,
          id: section.title,
          name: section.title,
          type: 'sectionTitle',
          children
        }
      ]
    }
  }
}

// Helper function to create a map of filter keys to their display names
export const createFilterKeyNameMap = (filterConfig) => {
  const filterKeys = Object.fromEntries(
    filterConfig?.data?.flatMap((category) => category.filters.map((filter) => [filter.key, filter.name])) || []
  )
  return filterKeys
}

export const formatUsecases = (data, filterKeyNameMap = {}) => {
  const convertToChildren = (node) => {
    let children = []
    if (Array.isArray(node.topics)) {
      children = node.topics
      node.type = 'domains'
    } else if (Array.isArray(node.subTopics)) {
      children = node.subTopics
      node.type = 'topics'
    } else if (Array.isArray(node.useCases)) {
      children = node.useCases
      node.type = 'subTopics'
      for (const child of children) {
        delete child.id
        child.hasTooltip = true
        child.type = 'useCases'
        child.id = getId(child.type, child.name)
        child.tooltipData = (
          <StyledTooltipContainer>
            <StyledTooltipTitle>Filters associated to this use case</StyledTooltipTitle>
            <StyledListContainer>
              {child.filters?.map((obj) => {
                return (
                  <StyledListItem key={child.name + obj.filterKey}>{filterKeyNameMap[obj.filterKey]}</StyledListItem>
                )
              })}
            </StyledListContainer>
          </StyledTooltipContainer>
        )
      }
    }
    return {
      ...node,
      id: getId(node.type, node.name),
      children: children.length > 0 ? children.map(convertToChildren) : []
    }
  }
  if (Array.isArray(data)) {
    return data.map((node) => ({
      ...convertToChildren(node),
      highlightRow: true
    }))
  }

  return convertToChildren(data)
}

export const formatPersonas = (config) => {
  if (config?.customization) {
    const personaInitialConfig = structuredClone(config.customization)
    return personaInitialConfig?.map((obj) => {
      const {type} = obj
      const addTypeToChildren = (items, parentType) => {
        return items.map((item) => ({
          ...item,
          type: parentType,
          children: item.children ? addTypeToChildren(item.children, parentType) : []
        }))
      }

      const children = addTypeToChildren(obj.children || [], type)

      return {
        ...obj,
        children,
        highlightRow: true
      }
    })
  }
  return []
}

export const formatPOIs = (config) => {
  if (config) {
    const data = Object.keys(config).map((key) => ({
      name: config[key].label,
      id: config[key].id,
      children: []
    }))
    return data
  }
}

export const processCustomLabelsResponse = (response, filterConfig) => {
  const customData = {}
  const {filters, pointOfInterestList, usecaseList, personaList} = response || {}

  // Generate section keys mapping from filter config
  const SECTION_KEYS = getSectionKeys(filterConfig)

  // Process filters using the new filters structure
  if (filters) {
    // eslint-disable-next-line camelcase
    const {title, custom_title, data, sections} = filters
    const filterCustomLabels =
      data?.reduce((acc, value) => {
        acc[value.FILTER_KEY] = {
          originalValue: value.FILTER_NAME,
          oldValue: value.FILTER_CUSTOM_NAME,
          newValue: value.FILTER_CUSTOM_NAME,
          id: value.FILTER_ID,
          key: value.FILTER_KEY,
          categoryId: value.FILTER_CATEGORY_ID,
          type: 'filter'
        }
        return acc
      }, {}) || {}

    // Process section titles if available
    const sectionTitles =
      sections?.reduce((acc, section) => {
        const sectionKey = section.title || section.name
        acc[sectionKey] = {
          originalValue: sectionKey,
          // eslint-disable-next-line camelcase
          oldValue: section.custom_title || '',
          // eslint-disable-next-line camelcase
          newValue: section.custom_title || '',
          isTitle: true,
          type: 'sectionTitle',
          key: section.key || SECTION_KEYS[sectionKey] || ''
        }
        return acc
      }, {}) || {}

    customData.filterCustomLabels = {
      [title || 'Filters']: {
        originalValue: title || 'Filters',
        // eslint-disable-next-line camelcase
        oldValue: custom_title || '',
        // eslint-disable-next-line camelcase
        newValue: custom_title || '',
        isTitle: true,
        type: 'mainTitle'
      },
      ...sectionTitles,
      ...filterCustomLabels
    }
  }

  if (usecaseList) {
    const usecaseSections = new Set(['domains', 'topics', 'subTopics', 'useCases'])
    const finalUsecaseData =
      Object.keys(usecaseList)
        ?.filter((key) => usecaseSections.has(key))
        ?.reduce((acc, key) => {
          const subData = (usecaseList[key] || [])?.reduce((subAcc, item) => {
            const id = getId(key, item.name)
            subAcc[id] = {
              ...item,
              id,
              originalValue: item.name,
              oldValue: item.custom_name,
              newValue: item.custom_name,
              type: key
            }
            return subAcc
          }, {})
          return {...acc, ...subData}
        }, {}) || {}
    customData.usecaseCustomLabels = finalUsecaseData
  }

  const {data} = personaList || {}

  const personaFinalData =
    data?.reduce((finalAcc, obj) => {
      const personaData =
        obj?.data?.reduce((acc, value) => {
          acc[value.name] = {
            originalValue: value.name,
            newValue: value.custom_name,
            oldValue: value.custom_name,
            name: value.name,
            type: obj.type
          }
          return acc
        }, {}) || {}

      finalAcc[obj.title] = {
        originalValue: obj.title,
        newValue: obj.custom_title,
        oldValue: obj.custom_title,
        name: obj.title,
        type: obj.type
      }

      return {...finalAcc, ...personaData}
    }, {}) || {}

  customData.personaCustomLabels = personaFinalData

  customData.poiCustomLabels =
    pointOfInterestList?.reduce((acc, value) => {
      acc[value.POI_ID] = {
        originalValue: value.POI_NAME,
        oldValue: value.POI_CUSTOM_NAME,
        newValue: value.POI_CUSTOM_NAME,
        id: value.POI_ID
      }
      return acc
    }, {}) || {}
  return customData
}

export const getFormattedCustomLabelsRequest = (customizationData, personasConfig, filterConfig) => {
  const customLabelDetails = []
  const {filterCustomLabels, usecaseCustomLabels, personaCustomLabels, poiCustomLabels} = customizationData || {}

  // Generate section keys mapping from filter config
  const SECTION_KEYS = getSectionKeys(filterConfig)

  // Process filters
  const filterData = Object.values(filterCustomLabels || {})
  const filterTitleData = filterData?.find((item) => item?.isTitle && item?.type === 'mainTitle')
  const filterNonTitleRecords = filterData.filter((item) => !item?.isTitle && item.newValue?.trim())

  // Always include all 7 section titles, even if not customized
  const allSectionTitles = [
    'Individual Profile',
    'Individual Health',
    'Financial Well-Being',
    'Home and Household',
    'Resource Access Barriers and Support',
    'Community Support and Location',
    'Environmental Exposure'
  ]

  const sections = allSectionTitles.map((sectionTitle) => {
    const customSection = filterData?.find(
      (item) => item?.isTitle && item?.type === 'sectionTitle' && item?.originalValue === sectionTitle
    )
    return {
      key: customSection?.key || SECTION_KEYS[sectionTitle] || '',
      title: sectionTitle,
      custom_title: customSection?.newValue || ''
    }
  })

  customLabelDetails.push({
    permissionId: PERMISSIONS.FILTERS,
    labels: {
      title: filterTitleData?.originalValue || '',
      custom_title: filterTitleData?.newValue || '',
      sections,
      data:
        filterNonTitleRecords?.map(({key, newValue, type}) => ({
          key,
          custom_name: newValue,
          type: type || 'filter'
        })) || []
    }
  })

  const useCaseData = Object.values(usecaseCustomLabels || {})?.filter((item) => item.newValue?.trim())
  customLabelDetails.push({
    permissionId: PERMISSIONS.USE_CASES,
    labels: {
      data:
        useCaseData?.map(({type, name, newValue}) => ({
          name,
          custom_name: newValue,
          type
        })) || []
    }
  })

  handlePersonaRequestInput(customLabelDetails, personaCustomLabels, personasConfig)

  const poiData = Object.values(poiCustomLabels || {})?.filter((item) => item.newValue?.trim())
  customLabelDetails.push({
    permissionId: PERMISSIONS.POINT_OF_INTERESTS,
    labels: {
      data:
        getValidData(poiData)?.map(({id, newValue}) => ({
          id: Number.parseInt(id),
          custom_name: newValue
        })) || []
    }
  })

  return customLabelDetails
}

const handlePersonaRequestInput = (customLabelDetails, personaCustomLabels, personaConfig) => {
  const personaData = Object.values(personaCustomLabels || {})?.filter((item) => item.newValue?.trim())
  const allCustomizedLabels =
    personaData?.map(({type, name, newValue}) => ({
      type,
      name,
      custom_name: newValue
    })) || []

  // Creating a deep copy to avoid mutating the original config
  const finalPersonaConfig = personaConfig ? structuredClone(personaConfig.customization) : null
  if (finalPersonaConfig) {
    const customizedMap = new Map(allCustomizedLabels.map((item) => [`${item.type}_${item.name}`, item]))

    const customLabels = finalPersonaConfig?.map((obj) => {
      const key = `${obj.type}_${obj.name}`
      const customization = customizedMap.get(key)
      obj.title = obj.name || ''
      // eslint-disable-next-line camelcase
      obj.custom_title = customization?.custom_name || ''
      obj.data =
        allCustomizedLabels
          ?.filter((dataItem) => dataItem.type === obj.type && dataItem.name !== obj.name)
          // eslint-disable-next-line camelcase
          ?.map(({name, custom_name}) => {
            return {
              name,
              // eslint-disable-next-line camelcase
              custom_name
            }
          }) || []
      delete obj.children
      delete obj.name
      return obj
    })

    if (customLabels.length > 0) {
      customLabelDetails.push({
        permissionId: PERMISSIONS.PERSONAS,
        labels: {
          data: customLabels
        }
      })
    }
  }
}

export const getCustomizationKey = (sectionName) => {
  switch (sectionName) {
    case SECTIONS.HEALTH_CONDITIONS:
      return 'heCustomLabels'
    case SECTIONS.COMMUNITY_FACTORS:
      return 'sdohCustomLabels'
    case SECTIONS.INDIVIDUAL_PROFILES:
    case SECTIONS.INDIVIDUAL_HEALTH_FILTERS:
    case SECTIONS.HOME_AND_HOUSEHOLD_FILTERS:
    case SECTIONS.FINANCIAL_WELLBEING:
    case SECTIONS.RESOURCE_ACCESS_BARRIERS:
    case SECTIONS.COMMUNITY_SUPPORT:
    case SECTIONS.ENVIRONMENTAL_EXPOSURE_FILTERS:
      return 'filterCustomLabels'
    case SECTIONS.USE_CASES:
      return 'usecaseCustomLabels'
    case SECTIONS.PERSONAS:
      return 'personaCustomLabels'
    case SECTIONS.POINT_OF_INTERESTS:
      return 'poiCustomLabels'
    default:
      return ''
  }
}

const hasChildren = (data) =>
  Array.isArray(data) && data.some((item) => Array.isArray(item.children) && item.children.length > 0)

export const hasAccordions = (data) => {
  if (!Array.isArray(data)) return false
  return data.some(
    (item) => (!item.isTitle && Array.isArray(item.children) && item.children.length > 0) || hasChildren(item.children)
  )
}

export const collectAllIds = (items) => {
  const allIds = []
  const collectIds = (items) => {
    for (const item of items) {
      const rowId = item.id || item.name
      allIds.push(rowId)
      if (item.children && item.children.length > 0) {
        collectIds(item.children)
      }
    }
  }
  collectIds(items)
  return allIds
}

export const checkAllRowsExpanded = (data, expandedRows) => {
  const allRows = [...data]
  const allIds = collectAllIds(allRows)
  return allIds.every((id) => expandedRows[id])
}

export const expandAllRows = (items, expanded = {}) => {
  for (const item of items) {
    const rowId = item.id || item.name
    expanded[rowId] = true
    if (item.children && item.children.length > 0) {
      expandAllRows(item.children, expanded)
    }
  }
  return expanded
}

export const hasDuplicateLabels = (labels) => {
  const values = Object.values(labels)
    .map((label) => label?.newValue?.trim().toLowerCase())
    .filter(Boolean)

  return values.length !== new Set(values).size
}

const collectAllDefaultLabels = (items, labels = []) => {
  for (const item of items) {
    if (item.name) {
      labels.push(item.name.trim().toLowerCase())
    }
    if (item.children && item.children.length > 0) {
      collectAllDefaultLabels(item.children, labels)
    }
  }
  return labels
}

export const hasConflictWithDefaultLabels = (customLabels, data) => {
  const defaultLabels = collectAllDefaultLabels(data)
  const customValues = Object.values(customLabels)
    .map((label) => label?.newValue?.trim().toLowerCase())
    .filter(Boolean)

  return customValues.some((customValue) => defaultLabels.includes(customValue))
}

export const hasDescendantCustomizations = (children, customLabels) => {
  if (!children || !Array.isArray(children)) return false

  return children.some((child) => {
    const childRowId = child.id || child.name
    const childHasCustomization = Boolean(customLabels[childRowId]?.newValue?.trim())

    return childHasCustomization || hasDescendantCustomizations(child.children, customLabels)
  })
}

export const hasValidationErrors = (error, customLabels, data = null) => {
  if (error) return true
  if (hasDuplicateLabels(customLabels)) return true
  if (data && hasConflictWithDefaultLabels(customLabels, data)) return true
  return Object.values(customLabels).some((label) => label?.newValue && label.newValue.length > 50)
}

export const hasChanges = (currentLabels, originalLabels) => {
  const currentKeys = Object.keys(currentLabels || {})
  const originalKeys = Object.keys(originalLabels || {})

  if (currentKeys.length !== originalKeys.length) {
    return true
  }

  return currentKeys.some((key) => {
    const current = currentLabels[key]?.newValue?.trim() || ''
    const original = originalLabels[key]?.newValue?.trim() || ''
    return current !== original
  })
}
